import React, { useState, useEffect, useMemo } from 'react';
import { Download, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NowName, { staffNow } from './shared/NowName';
import { exportToExcel } from '../utils/excelExport';
import { formatDate, formatDateShort } from '../utils/dateFormat';
import DashboardTabHeader from './shared/DashboardTabHeader';
import { PAGE_SIZE, TablePager } from './SortableTable';
import { exportExcelBtnCls } from './shared/ExportExcelButton';

const parseDMY = (str) => {
  if (!str) return null;
  const m = String(str).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(`${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`);
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

// ISO 'YYYY-MM-DD' from the API → display 'DD-MM-YYYY' (string swap, no TZ shift)
const formatDMY = (str) => (str ? formatDate(str) : null);
  const formatDMYShort = (str) => (str ? formatDateShort(str) : null);

const SortTh = ({ label, field, sortField, sortDir, onSort, className = '' }) => (
  <th
    onClick={() => onSort(field)}
    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider group cursor-pointer select-none hover:bg-gray-100 ${className}`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {sortField === field
        ? (sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)
        : <ChevronsUpDown className="w-3.5 h-3.5 opacity-30 group-hover:opacity-60" />}
    </span>
  </th>
);

const CyberSecurity = () => {
  const { hasPermission } = useAuth();
  const canExport = hasPermission('qm:view');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    reportingOfficer: 'all',
    calendarYear: 'all',
    searchQuery: ''
  });
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/qm/registers/cyber/stats`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch cyber security data');
      }
      const result = await response.json();
      setStats(result.data);
    } catch (err) {
      console.error('Error fetching cyber security data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 一個欄位的去重選項。人名欄（`staffField`）認現名——同一人改名後舊記錄與新
  // 記錄的快照不同，拿快照當選項會列出兩筆（docs/data-model.md §2）。
  const getUniqueValues = (field, staffField = false) => {
    if (!stats || !stats.records) return [];
    const read = (r) => (staffField ? staffNow(r[field], r[`${field}Now`]) : r[field]) || 'Unknown';
    return [...new Set(stats.records.map(read))].sort();
  };

  const filteredRecords = useMemo(() => {
    if (!stats || !stats.records) return [];
    return stats.records.filter(record => {
      if (filters.reportingOfficer !== 'all') {
        if ((staffNow(record.reportingOfficer, record.reportingOfficerNow) || 'Unknown') !== filters.reportingOfficer) return false;
      }
      if (filters.calendarYear !== 'all') {
        const d = parseDMY(record.dateReported);
        const year = d ? String(d.getFullYear()) : 'Unknown';
        if (year !== filters.calendarYear) return false;
      }
      if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase();
        const entity = (record.entityInvolved || '').toLowerCase();
        const nature = (record.nature || '').toLowerCase();
        if (!entity.includes(q) && !nature.includes(q)) return false;
      }
      return true;
    });
  }, [stats, filters]);

  const sortedRecords = useMemo(() => {
    if (!sortField) return filteredRecords;
    return [...filteredRecords].sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (sortField === 'dateReported') {
        const aD = parseDMY(aVal) || new Date(0);
        const bD = parseDMY(bVal) || new Date(0);
        return sortDir === 'asc' ? aD - bD : bD - aD;
      }
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [filteredRecords, sortField, sortDir]);

  const calendarYearOptions = stats && stats.records
    ? [...new Set(stats.records.map(r => {
        const d = parseDMY(r.dateReported);
        return d ? String(d.getFullYear()) : null;
      }).filter(Boolean))].sort().reverse()
    : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading cyber security data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold mb-2">Error Loading Cyber Security Data</h3>
        <p className="text-red-600">{error}</p>
        <button onClick={fetchData} className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">No cyber security data available.</p>
      </div>
    );
  }

  const paginated = sortedRecords.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const officerOptions = getUniqueValues('reportingOfficer', true);

  return (
    <div className="space-y-6">
      {/* Header — unified tab header (docs/dashboard-ui.md) */}
      <DashboardTabHeader
        description="Register of reported cyber security incidents"
        actions={canExport && (
          <button
            onClick={() => {
              const rows = filteredRecords.map(r => ({
                'Date Reported': formatDMY(r.dateReported) || '',
                'Reporting Officer': staffNow(r.reportingOfficer, r.reportingOfficerNow) || '',
                'Entity Involved': r.entityInvolved || '',
                'Nature': r.nature || '',
              }));
              exportToExcel(rows, 'Cyber_Security_Report.xlsx');
            }}
            className={exportExcelBtnCls}
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
        )}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="text-gray-600 text-sm">Total Incidents</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-indigo-500">
          <div className="text-gray-600 text-sm">Showing (filtered)</div>
          <div className="text-3xl font-bold text-indigo-700 mt-2">{filteredRecords.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="text-gray-600 text-sm">Most Recent</div>
          <div className="text-lg font-bold text-green-700 mt-2">
            {stats.records && stats.records.length > 0 ? (formatDMY(stats.records[0].dateReported) || 'N/A') : 'N/A'}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={filters.searchQuery}
              onChange={(e) => setFilter('searchQuery', e.target.value)}
              placeholder="Search entity or nature..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Officer</label>
            <select
              value={filters.reportingOfficer}
              onChange={(e) => setFilter('reportingOfficer', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Officers</option>
              {officerOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Calendar Year</label>
            <select
              value={filters.calendarYear}
              onChange={(e) => setFilter('calendarYear', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Years</option>
              {calendarYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredRecords.length} of {stats.total} incidents
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortTh label="Date Reported" field="dateReported" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Reporting Officer" field="reportingOfficer" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Entity Involved" field="entityInvolved" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Nature" field="nature" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">No incidents match the selected filters</td>
                </tr>
              ) : (
                paginated.map((record, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDMYShort(record.dateReported) || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"><NowName value={record.reportingOfficer} now={record.reportingOfficerNow} fallback="N/A" /></td>
                    <td className="px-6 py-4 text-sm text-gray-900">{record.entityInvolved || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 min-w-[260px] whitespace-pre-wrap break-words">{record.nature || 'N/A'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePager
          page={page}
          setPage={setPage}
          total={sortedRecords.length}
          className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50"
        />
      </div>
    </div>
  );
};

export default CyberSecurity;
